import {
  ArrowRight,
  Clock,
  KeyRound,
  LayoutDashboard,
  Plus,
  ShieldAlert,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { problemMessage } from '../api/problem';
import { InvitePanel } from '../invitations/InvitePanel';
import { OrganizationDetailsCard } from '../organizations/OrganizationDetailsCard';
import { usePlaceContext } from '../place/PlaceLayout';
import { apiPlace, sectionPath, type SectionId } from '../place/place';
import { useRoleText } from '../roles/role-text';
import { PageHead } from '../ui/PageHead';
import { PhonePrimary } from '../ui/PhonePrimary';
import { inheritedFrom, useExpiryText } from '../ui/person-bits';
import { StateMessage } from '../ui/StateMessage';
import { useSummary, type InheritedRole, type PlaceSummary } from './use-summary';

/** Where a number or a row leads: a section this caller has here, or nowhere. */
type To = (section: SectionId, query?: string) => string | null;

/**
 * Riepilogo (FR-AD-8, the third round of boards in decision 61): the place at a glance, at a tenant
 * or an organization. Four numbers, each the count of a section's own list and each leading to it,
 * then what needs attention, each row a target the console opens. A number or a row leads only
 * where this caller has the section.
 */
export function SummaryPage() {
  const { place, detail, sections } = usePlaceContext();
  const pair = apiPlace(place);
  const placeKind = pair?.placeKind ?? 'organizations';
  const placeId = pair?.placeId ?? '';
  const placeName = detail.place.name;
  const { state, reload } = useSummary(placeKind, placeId);
  const [inviting, setInviting] = useState(false);
  const titleId = useId();
  const to: To = (section, query = '') =>
    sections.includes(section) ? `${sectionPath(place, section)}${query}` : null;
  const grantTo = to('roles', '?grant=1');

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-5">
      <SummaryHead
        titleId={titleId}
        placeName={placeName}
        grantTo={grantTo}
        canInvite={detail.can.invite}
        onInvite={() => setInviting(true)}
      />
      <SummaryBody
        state={state}
        reload={reload}
        placeName={placeName}
        atOrganization={place.kind === 'organization'}
        to={to}
      />
      {/* Decision 192: what Note spese prints in the PDF's company block, at an organization only. */}
      {place.kind === 'organization' && (
        <OrganizationDetailsCard key={place.id} organizationId={place.id} />
      )}
      <SummaryInvite
        canInvite={detail.can.invite}
        inviting={inviting}
        setInviting={setInviting}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        reload={reload}
      />
    </section>
  );
}

function SummaryHead({
  titleId,
  placeName,
  grantTo,
  canInvite,
  onInvite,
}: {
  readonly titleId: string;
  readonly placeName: string;
  readonly grantTo: string | null;
  readonly canInvite: boolean;
  readonly onInvite: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <PageHead
      titleId={titleId}
      title={t('summary.title')}
      summary={t('summary.summary', { place: placeName })}
      actions={
        <>
          {grantTo === null ? null : (
            <span className="page-primary">
              <Button asChild variant="outline">
                <Link to={grantTo}>
                  <KeyRound />
                  {t('summary.grant')}
                </Link>
              </Button>
            </span>
          )}
          {canInvite ? (
            <span className="page-primary">
              <Button onClick={() => onInvite()}>
                <Plus />
                {t('people.invite')}
              </Button>
            </span>
          ) : null}
        </>
      }
    />
  );
}

function SummaryBody({
  state,
  reload,
  placeName,
  atOrganization,
  to,
}: {
  readonly state: ReturnType<typeof useSummary>['state'];
  readonly reload: () => void;
  readonly placeName: string;
  readonly atOrganization: boolean;
  readonly to: To;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-5">
        <div className="summary-counts">
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-32 w-full" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
      </SkeletonRegion>
    );
  }
  if (state.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={LayoutDashboard}
        title={t('summary.loadError')}
        text={problemMessage(t, state.code, state.httpStatus)}
        action={
          <Button variant="outline" onClick={reload}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  }
  return (
    <>
      <SummaryCounts
        summary={state.summary}
        placeName={placeName}
        atOrganization={atOrganization}
        to={to}
      />
      <AttentionLists summary={state.summary} to={to} />
    </>
  );
}

function SummaryInvite({
  canInvite,
  inviting,
  setInviting,
  placeKind,
  placeId,
  placeName,
  reload,
}: {
  readonly canInvite: boolean;
  readonly inviting: boolean;
  readonly setInviting: (open: boolean) => void;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  if (!canInvite) return null;
  return (
    <>
      <PhonePrimary>
        <Button onClick={() => setInviting(true)}>
          <Plus />
          {t('people.inviteShort')}
        </Button>
      </PhonePrimary>
      <InvitePanel
        open={inviting}
        onOpenChange={setInviting}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        onInvited={reload}
      />
    </>
  );
}

function SummaryCounts({
  summary,
  placeName,
  atOrganization,
  to,
}: {
  readonly summary: PlaceSummary;
  readonly placeName: string;
  readonly atOrganization: boolean;
  readonly to: To;
}) {
  const { t } = useTranslation('administration');
  const { t: tShell } = useTranslation('shell');
  const expiry = useExpiryText();
  const { people, pendingInvitations, grantsHere, activeModules } = summary.counts;
  const firstExpiresAt = pendingInvitations.firstExpiresAt;
  const here = t('summary.modulesHere', { count: activeModules.here });

  return (
    <ul className="summary-counts" aria-label={t('summary.countsLabel', { place: placeName })}>
      <CountCard
        label={t('summary.people')}
        value={people}
        to={to('people')}
        link={tShell('nav.adminSections.people')}
      />
      <CountCard
        label={t('summary.pendingInvitations')}
        value={pendingInvitations.count}
        sub={
          firstExpiresAt === null
            ? t('summary.noneWaiting')
            : t('summary.firstExpires', { expiry: expiry(firstExpiresAt) })
        }
        // Inviti lists every invitation; the number counts the pending ones, soonest first.
        to={to('invitations', '?status=pending&sort=expires')}
        link={tShell('nav.adminSections.invitations')}
      />
      <CountCard
        label={t('summary.grantsHere')}
        value={grantsHere.direct}
        sub={
          grantsHere.inherited > 0
            ? t('summary.inherited', { count: grantsHere.inherited })
            : undefined
        }
        to={to('roles')}
        link={tShell('nav.adminSections.roles')}
      />
      <CountCard
        label={t('summary.activeModules')}
        value={activeModules.here + activeModules.fromTenant}
        sub={
          atOrganization
            ? `${here}, ${t('summary.modulesFromTenant', { count: activeModules.fromTenant })}`
            : here
        }
        to={to('modules')}
        link={tShell('nav.adminSections.modules')}
      />
    </ul>
  );
}

/** One number: a link to its list when this caller has the list, a plain card otherwise. */
function CountCard({
  label,
  value,
  sub,
  to,
  link,
}: {
  readonly label: string;
  readonly value: number;
  readonly sub?: string | undefined;
  readonly to: string | null;
  readonly link: string;
}) {
  const body = (
    <>
      <span className="summary-count-label">{label}</span>
      <span className="summary-count-value">{value}</span>
      {sub === undefined ? null : <span className="summary-count-sub">{sub}</span>}
    </>
  );
  return (
    <li>
      {to === null ? (
        <div className="summary-count">{body}</div>
      ) : (
        <Link to={to} className="summary-count">
          {body}
          <span className="summary-count-link">
            {link}
            <ArrowRight aria-hidden="true" />
          </span>
        </Link>
      )}
    </li>
  );
}

type Attention = PlaceSummary['attention'];

function ExpiringItems({
  invitations,
  action,
}: {
  readonly invitations: Attention['expiringInvitations'];
  readonly action: string | null;
}) {
  const { t } = useTranslation('administration');
  const expiry = useExpiryText();
  return (
    <>
      {invitations.items.map((invitation) => (
        <AttentionItem
          key={`invitation-${invitation.id}`}
          icon={Clock}
          text={t('summary.attention.expiring', {
            who: invitation.displayName ?? invitation.email,
            expiry: expiry(invitation.expiresAt),
          })}
          action={action}
          actionLabel={t('summary.attention.seeInvitations')}
        />
      ))}
      <MoreItem count={invitations.count - invitations.items.length} to={action} />
    </>
  );
}

function InheritedItems({
  group,
  sheet,
  peopleList,
}: {
  readonly group: Attention['peopleWithoutDirectGrant'];
  readonly sheet: (personId: string) => string | null;
  readonly peopleList: string | null;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  return (
    <>
      {group.items.map(({ person, roles }) => (
        <AttentionItem
          key={`inherited-${person.id}`}
          icon={Users}
          text={inheritedText(t, person.displayName, roles, name)}
          action={sheet(person.id)}
          actionLabel={t('summary.attention.grant')}
        />
      ))}
      <MoreItem count={group.count - group.items.length} to={peopleList} />
    </>
  );
}

function DeactivatedItems({
  group,
  sheet,
  peopleList,
}: {
  readonly group: Attention['deactivatedWithGrants'];
  readonly sheet: (personId: string) => string | null;
  readonly peopleList: string | null;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  return (
    <>
      {group.items.map(({ person, roles }) => (
        <AttentionItem
          key={`deactivated-${person.id}`}
          icon={ShieldAlert}
          text={
            roles.length === 1 && roles[0] !== undefined
              ? t('summary.attention.deactivatedOne', {
                  name: person.displayName,
                  role: name(roles[0]),
                })
              : t('summary.attention.deactivatedMany', {
                  name: person.displayName,
                  count: roles.length,
                })
          }
          action={sheet(person.id)}
          actionLabel={t('summary.attention.openSheet')}
        />
      ))}
      <MoreItem count={group.count - group.items.length} to={peopleList} />
    </>
  );
}

function AttentionLists({ summary, to }: { readonly summary: PlaceSummary; readonly to: To }) {
  const { t } = useTranslation('administration');
  const titleId = useId();
  const { expiringInvitations, peopleWithoutDirectGrant, deactivatedWithGrants } =
    summary.attention;
  const total =
    expiringInvitations.count + peopleWithoutDirectGrant.count + deactivatedWithGrants.count;
  const invitations = to('invitations', '?status=pending&sort=expires');
  const peopleList = to('people');
  const sheet = (personId: string) =>
    peopleList === null ? null : `${to('people')}/${encodeURIComponent(personId)}`;

  return (
    <section aria-labelledby={titleId} className="summary-attention">
      <h3 id={titleId} className="summary-attention-title">
        {t('summary.attention.title')}
        {total > 0 ? <span className="summary-attention-count">{total}</span> : null}
      </h3>
      {total === 0 ? (
        <div className="summary-attention-empty">
          <p className="text-foreground font-semibold">{t('summary.attention.emptyTitle')}</p>
          <p className="text-muted-foreground text-sm">{t('summary.attention.emptyText')}</p>
        </div>
      ) : (
        <ul className="summary-attention-list">
          <ExpiringItems invitations={expiringInvitations} action={invitations} />
          <InheritedItems group={peopleWithoutDirectGrant} sheet={sheet} peopleList={peopleList} />
          <DeactivatedItems group={deactivatedWithGrants} sheet={sheet} peopleList={peopleList} />
        </ul>
      )}
    </section>
  );
}

/**
 * Someone here only through a department or a group: the role by name when there is one, the
 * place it comes from when every role comes from the same one, and in general words otherwise.
 */
function inheritedText(
  t: TFunction<'administration'>,
  personName: string,
  roles: readonly InheritedRole[],
  roleName: (role: InheritedRole['role']) => string,
): string {
  const [first] = roles;
  const sources = new Set(roles.map((role) => `${role.via}:${role.viaName ?? ''}`));
  if (first === undefined || sources.size > 1) {
    return t('summary.attention.inheritedMixed', { name: personName });
  }
  const source = inheritedFrom(t, first, 'chip') ?? '';
  return roles.length === 1
    ? t('summary.attention.inheritedOne', {
        name: personName,
        role: roleName(first.role),
        source,
      })
    : t('summary.attention.inheritedMany', { name: personName, source });
}

function AttentionItem({
  icon: Icon,
  text,
  action,
  actionLabel,
}: {
  readonly icon: LucideIcon;
  readonly text: string;
  readonly action: string | null;
  readonly actionLabel: string;
}) {
  return (
    <li className="summary-attention-item">
      <span className="summary-attention-icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="summary-attention-body">
        <span className="summary-attention-text">{text}</span>
        {action === null ? null : (
          <Button asChild variant="outline" size="sm">
            <Link to={action}>{actionLabel}</Link>
          </Button>
        )}
      </span>
    </li>
  );
}

/** «e altri N»: the list holds at most five, and says how many more there are. */
function MoreItem({ count, to }: { readonly count: number; readonly to: string | null }) {
  const { t } = useTranslation('administration');
  if (count <= 0) {
    return null;
  }
  return (
    <li className="summary-attention-more">
      <span>{t('summary.attention.more', { count })}</span>
      {/* A button, not a text link: on a phone it gets the area's 44 px like every other target. */}
      {to === null ? null : (
        <Button asChild variant="ghost" size="sm">
          <Link to={to}>{t('summary.attention.seeAll')}</Link>
        </Button>
      )}
    </li>
  );
}
